import React, { lazy, Suspense, useState, useEffect } from "react";
import {
  ArrowRightIcon,
  BadgeCheckIcon,
  CheckIcon,
  ChevronDownIcon,
  GiftIcon,
  MessageSquareTextIcon,
  PhoneIcon,
  ShieldCheckIcon,
  TimerIcon,
  XIcon,
  ZapIcon,
  BellRingIcon,
  InboxIcon,
  PlugIcon,
} from "lucide-react";

import { ChatPhone } from "../Components/Diwalipage/ChatPhone";
import { Diya, LightString } from "../Components/Diwalipage/DiwaliDecor";
import { Marquee } from "../Components/Diwalipage/Marquee"
import { SectionHeading } from "../Components/Diwalipage/SectionHeading";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../Components/Diwalipage/Accordion";

import { buttonVariants } from "../Components/Diwalipage/Button";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../Components/Diwalipage/Collapsible";

import { ToggleGroup, ToggleGroupItem } from "../Components/Diwalipage/ToggleGroup";

import { site } from "../config/site";
import { now, isExpired, daysLeft } from "../lib/offer";
import { trackEvent } from "../lib/tracking";
import { usePageView } from "../lib/usePageView";
import { cn } from "../lib/utils";

import {
  comparison,
  faq,
  finalCta,
  hero,
  heroChat,
  industries,
  offer,
  OFFER_END,
  price,
  speed,
  steps,
  teaseCaption,
  teaseMessages,
  trust,
} from "../Content";

import metaLogo from "../Images/meta-tech-provider.png";
import { SiteHeader } from "../Components/Diwalipage/SiteHeader";
import { SiteFooter } from "../Components/Diwalipage/SiteFooter";

// The form (validation, phone metadata, country search) loads as its own chunk.
const LeadForm = lazy(() =>
  import("../Components/Diwalipage/LeadForm").then((m) => ({ default: m.LeadForm }))
)

const CLAIM = "#claim"
const container = "mx-auto w-full max-w-[1140px] px-4 sm:px-6"

function OfferTag({ children, className }) {
  return (
    <p
      className={cn(
        "inline-flex w-fit items-center gap-1.5 rounded-lg border border-campaign/30 bg-campaign-tint px-3 py-1.5 text-xs font-semibold text-campaign-text",
        className
      )}
    >
      <Diya className="-my-1 size-5" animated={false} />
      {children}
    </p>
  );
}

function CallLink({ location, className, dark }) {
  return (
    <a
      href={site.phoneHref}
      onClick={() => trackEvent("call_click", { location })}
      className={cn(
        buttonVariants({ variant: "outline", size: "lg" }),
        dark &&
          "border-white/30 bg-transparent text-on-dark shadow-none hover:bg-white/10 hover:text-white",
        className
      )}
    >
      <PhoneIcon data-icon="inline-start" aria-hidden="true" />
      Call {site.phoneDisplay}
    </a>
  );
}

export function DiwaliOffer() {
  usePageView();

  const [currentTime, setCurrentTime] = useState(() => now());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(now());
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const expired = isExpired(OFFER_END, currentTime);
  const left = daysLeft(OFFER_END, currentTime);

  const daysLabel = left === 1 ? "Last day" : `${left} days left`;

  const [industryId, setIndustryId] = useState(industries.items[0].id);

  const industry =
    industries.items.find((i) => i.id === industryId) ?? industries.items[0];

  const heroItems = [
    { from: "customer", text: heroChat.customer, time: "11:42 PM" },
    {
      from: "business",
      text: heroChat.reply,
      buttons: heroChat.buttons,
      time: "11:42 PM",
    },
    { from: "label", text: heroChat.reminderLabel },
    { from: "business", text: heroChat.reminder, time: "10:00 AM" },
  ];

  const industryItems = [
    { from: "customer", text: industry.ask },
    { from: "business", text: industry.reply, buttons: industry.buttons },
    { from: "label", text: `${industry.reminderLabel} · reminder` },
    { from: "business", text: industry.reminder },
  ];

  const stepIcons = [
    MessageSquareTextIcon,
    ZapIcon,
    BellRingIcon,
    InboxIcon,
  ];

  const trustIcons = [ShieldCheckIcon, GiftIcon, BadgeCheckIcon];

  useEffect(() => {
      document.title =
        "Diwali WhatsApp Business API Offer | Onbbits";
  
      const metaDesc = document.querySelector("meta[name='description']");
      if (metaDesc) {
        metaDesc.setAttribute(
          "content",
          "Get the official WhatsApp Business API for ₹2,499/month this Diwali. Enjoy free setup, ready-made automation flows and tools to grow your business."
        );
      }
  
       const canonical = document.querySelector("link[rel='canonical']");
    if (canonical) {
      canonical.setAttribute("href", "https://onbbits.io/diwali-offer");
    }
  
    const ogTitle = document.querySelector("meta[property='og:title']");
    if (ogTitle) {
      ogTitle.setAttribute(
        "content",
        "Diwali WhatsApp Business API Offer | Onbbits"
      );
    }
  
      const ogDesc = document.querySelector("meta[property='og:description']");
    if (ogDesc) {
      ogDesc.setAttribute(
        "content",
        "Get the official WhatsApp Business API for ₹2,499/month this Diwali. Enjoy free setup, ready-made automation flows and tools to grow your business."
      );
    }
  
    const ogUrl = document.querySelector("meta[property='og:url']");
    if (ogUrl) {
      ogUrl.setAttribute("content", "https://onbbits.io/diwali-offer");
    }
  
      const ogImage = document.querySelector("meta[property='og:image']");
    if (ogImage) {
      ogImage.setAttribute(
        "content",
        "https://onbbits.io/Images/flow-builder.webp"
      );
    }
  
      const twitterTitle = document.querySelector("meta[name='twitter:title']");
    if (twitterTitle) {
      twitterTitle.setAttribute(
        "content",
        "Diwali WhatsApp Business API Offer | Onbbits"
      );
    }
  
      const twitterDesc = document.querySelector(
      "meta[name='twitter:description']"
    );
    if (twitterDesc) {
      twitterDesc.setAttribute(
        "content",
        "Get the official WhatsApp Business API for ₹2,499/month this Diwali. Enjoy free setup, ready-made automation flows and tools to grow your business."
      );
    }
  
      const twitterImage = document.querySelector("meta[name='twitter:image']");
    if (twitterImage) {
      twitterImage.setAttribute(
        "content",
        "https://onbbits.io/Images/flow-builder.webp"
      );
    }
    }, [])

    return(
        <>

         <SiteHeader
        ctaLabel={expired ? "See pricing" : hero.cta}
        ctaHref={expired ? site.links.pricing : CLAIM}
        hideCtaOnMobile={!expired}
      />

         <main>
        {/* 1. Hero */}
      <section className=" relative overflow-hidden
    max-[574px]:-mt-2
    min-[575px]:max-[767px]:-mt-4
    min-[768px]:max-[991px]:-mt-[30px]
    min-[992px]:max-[1023px]:-mt-[50px]
    min-[1024px]:max-[1199px]:-mt-[50px]
    min-[1200px]:max-[1399px]:-mt-[64px]
    min-[1400px]:-mt-[64px]
    pt-0 pb-16 md:pt-40 md:pb-24">
          <div
            className="bg-hero-grid pointer-events-none absolute inset-x-0 top-0 h-[560px] [mask-image:linear-gradient(to_bottom,#000_60%,transparent)]"
            aria-hidden="true"
          />
          <div
            className="bg-hero-spotlight pointer-events-none absolute inset-0"
            aria-hidden="true"
          />
          {!expired && (
            <>
              <div
                className="bg-diwali-glow pointer-events-none absolute inset-0"
                aria-hidden="true"
              />
              {/* Festive light string hanging just below the floating header */}
 <div className="pointer-events-none absolute top-0 left-0 z-10 w-full md:top-1 lg:top-0 max-[640px]:top-0">
  <div className="light-string-container">
    <LightString className="w-full" />
  </div>
</div>
            </>
          )}
        <div
  className={cn(
    container,
    "relative -mt-6 sm:mt-10 md:-mt-12 lg:-mt-16 grid items-center gap-12 lg:grid-cols-[1.3fr_0.7fr] max-[640px]:mt-14"
  )}
>
            <div className="flex flex-col items-start gap-1">
            <img
  src={metaLogo}
  alt="Meta Tech Provider | Onbbits"
  className="h-14 w-auto object-contain max-[640px]:h-10"
/>
              {expired ? (
                <>
                  <h1 className="text-[30px] leading-[1.3] font-normal sm:text-[36px] lg:text-[44px] lg:leading-[1.15] lg:tracking-[-1px] xl:text-[52px]">
                    {hero.ended.h1}
                  </h1>
                  <p className="text-base leading-[1.85] font-light text-muted-foreground sm:text-lg">
                    {hero.ended.sub}
                  </p>
                  <a
                    href={site.links.pricing}
                    className={buttonVariants({ size: "lg" })}
                  >
                    {hero.ended.cta}
                    <ArrowRightIcon data-icon="inline-end" aria-hidden="true" />
                  </a>
                </>
              ) : (
                <>
                 <div className="flex flex-row items-start gap-2 max-[640px]:flex-wrap">
  <OfferTag>Diwali offer · Ends 11 Nov · {daysLabel}</OfferTag>

  <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium shadow-card">
    <ShieldCheckIcon
      className="size-3.5 text-brand-text"
      aria-hidden="true"
    />
    Official WhatsApp Business API
  </span>
</div>
                  <h1 className="w-full max-w-none text-[30px] leading-[1.3] font-normal text-balance sm:text-[36px] sm:leading-[1.25] lg:text-[44px] lg:leading-[1.15] lg:tracking-[-1px] xl:text-[52px]">
                    This{" "}
                    <span className="text-gradient-diwali font-medium">
                      {hero.h1Festival}
                    </span>
                    {hero.h1Rest}{" "}
                    <span className="text-gradient-brand font-medium">
                      {hero.h1Accent}
                    </span>
                  </h1>
                  <p className="max-w-xl text-base leading-[1.85] font-light text-ink-strong sm:text-lg">
                    {hero.sub}
                  </p>
                  <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span
                      className="text-lg text-muted-foreground line-through decoration-1"
                      aria-label={`Regular price ${price.regular} per month`}
                    >
                      {price.regular}
                    </span>
                    <span className="text-3xl font-semibold tracking-tight sm:text-4xl">
                      {price.offer}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {hero.priceSuffix}
                    </span>
                    <span className="rounded-md bg-brand-tint px-2 py-0.5 text-xs font-semibold text-brand-text">
                      Save {price.savingTotal}
                    </span>
                  </p>
                  <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                    <a
                      href={CLAIM}
                      className={buttonVariants({
                        size: "lg",
                        className: "w-full sm:w-auto",
                      })}
                    >
                      {hero.cta}
                      <ArrowRightIcon
                        data-icon="inline-end"
                        aria-hidden="true"
                      />
                    </a>
                    <CallLink location="hero" className="w-full sm:w-auto" />
                  </div>
                  <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {hero.microcopy.map((m) => (
                      <li key={m} className="flex items-center gap-1.5">
                        <CheckIcon
                          className="size-4 text-brand-text"
                          aria-hidden="true"
                        />
                        {m}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <div className="relative">
              <div
                className="pointer-events-none absolute -inset-6 rounded-full bg-brand/10 blur-3xl"
                aria-hidden="true"
              />
              <ChatPhone
                business={heroChat.business}
                items={heroItems}
                className="relative"
              />
              {!expired && (
                <Diya className="absolute bottom-8 -left-1 z-10 size-16 drop-shadow-[0_6px_14px_rgba(234,88,12,0.35)] sm:-left-4 sm:size-20 lg:-left-12" />
              )}
              <span className="absolute -top-4 left-1/2 z-10 hidden -translate-x-[85%] items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium whitespace-nowrap shadow-card-raised sm:inline-flex">
                <ZapIcon className="size-3.5 text-brand" aria-hidden="true" />
                {hero.chips[0]}
              </span>
              <span className="absolute -bottom-4 left-1/2 z-10 hidden -translate-x-[15%] items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium whitespace-nowrap shadow-card-raised sm:inline-flex">
                <BellRingIcon
                  className="size-3.5 text-brand"
                  aria-hidden="true"
                />
                {hero.chips[1]}
              </span>
            </div>
          </div>
        </section>

        {/* After the deadline the page is hero-only, so no stale offer price can show. */}
        {!expired && (
          <>
            {/* 2. Tease strip */}
            <Marquee items={teaseMessages} caption={teaseCaption} />

            {/* 3. Trust strip */}
            <section
              aria-label="Why Onbbits"
              className={cn(container, "grid gap-4 py-12 md:py-16 sm:grid-cols-3")}
            >
              {trust.map((t, i) => {
                const Icon = trustIcons[i]
                return (
                  <div
                    key={t.title}
                    className="flex items-start gap-3 rounded-lg border border-border bg-background p-4 shadow-card"
                  >
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand-tint">
                      <Icon
                        className="size-5 text-brand-text"
                        aria-hidden="true"
                      />
                    </span>
                    <p className="text-sm leading-relaxed">
                      <span className="block font-semibold">{t.title}</span>
                      <span className="text-muted-foreground">{t.text}</span>
                    </p>
                  </div>
                )
              })}
            </section>

            {/* 4. Speed stat */}
            <section className={cn(container, "py-12 md:py-16")}>
              <div className="grid items-center gap-10 rounded-lg border border-border bg-surface-cream p-6 shadow-card sm:p-10 lg:grid-cols-2">
                <SectionHeading
                  align="left"
                  eyebrow={speed.eyebrow}
                  lead={speed.h2Lead}
                  accent={speed.h2Accent}
                  sub={speed.body}
                />
                <div className="flex flex-col gap-5 rounded-lg border border-border bg-background p-6 shadow-card-raised">
                  <p className="flex items-baseline gap-3">
                    <span className="text-gradient-stat text-6xl font-semibold tracking-tight sm:text-7xl">
                      {speed.stat}
                    </span>
                    <TimerIcon
                      className="size-6 text-brand"
                      aria-hidden="true"
                    />
                  </p>
                  <p className="text-lg leading-snug">{speed.statText}</p>
                  <div className="flex flex-col gap-2" aria-hidden="true">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="w-20 shrink-0 text-muted-foreground">
                        5 minutes
                      </span>
                      <span className="h-3 flex-1 rounded-full bg-brand" />
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="w-20 shrink-0 text-muted-foreground">
                        30 minutes
                      </span>
                      <span className="h-3 w-[5%] rounded-full bg-ink-strong/30" />
                    </div>
                  </div>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Source: {speed.source}
                  </p>
                </div>
              </div>
            </section>

            {/* 5. How it works */}
            <section className="bg-mint py-16 md:py-20">
              <div className={cn(container, "flex flex-col gap-10")}>
                <SectionHeading
                  eyebrow={steps.eyebrow}
                  lead={steps.h2Lead}
                  accent={steps.h2Accent}
                />
                <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {steps.items.map((s, i) => {
                    const Icon = stepIcons[i]
                    return (
                      <li
                        key={s.title}
                        className="flex flex-col gap-3 rounded-lg border border-border bg-background p-6 shadow-card"
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex size-12 items-center justify-center rounded-lg border border-border bg-background shadow-card">
                            <Icon
                              className="size-5 text-brand-text"
                              aria-hidden="true"
                            />
                          </span>
                          <span className="text-gradient-stat text-2xl font-semibold">
                            0{i + 1}
                          </span>
                        </div>
                        <h3 className="text-lg font-medium">{s.title}</h3>
                        <p className="text-sm leading-relaxed font-light text-ink-strong">
                          {s.text}
                        </p>
                        <p className="mt-auto rounded-lg bg-surface-soft px-3 py-2 text-sm text-muted-foreground italic">
                          {s.example}
                        </p>
                      </li>
                    )
                  })}
                </ol>
              </div>
            </section>

            {/* 6. Offer + form */}
            <section id="claim" className="scroll-mt-24 py-16 md:py-24">
              <div className={cn(container, "flex flex-col gap-10")}>
                <div className="flex flex-col items-center gap-4">
                  {!expired && <OfferTag>{offer.tag}</OfferTag>}
                  <SectionHeading lead={offer.h2Lead} accent={offer.h2Accent} />
                </div>
                <div className="grid items-start gap-6 lg:grid-cols-2">
                  {/* Price card */}
                  <div className="flex flex-col gap-5 rounded-lg border-2 border-[#00b538] bg-background p-6 shadow-featured sm:p-7">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-xl font-semibold">
                        {offer.card.name}
                      </h3>
                      <span className="rounded-md bg-campaign-tint px-2.5 py-1 text-xs font-semibold text-campaign-text">
                        {offer.card.badge}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {offer.card.descriptor}
                    </p>
                    <div>
                      <p className="flex flex-wrap items-baseline gap-x-3">
                        <span
                          className="text-lg text-muted-foreground line-through"
                          aria-label={`Regular price ${price.regular} per month`}
                        >
                          {price.regular}/month
                        </span>
                        <span className="text-4xl font-semibold tracking-tight">
                          {price.offer}
                        </span>
                        <span className="text-muted-foreground">/month</span>
                      </p>
                      <p className="mt-1 text-sm font-medium text-brand-text">
                        {offer.card.underPrice}
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {offer.card.terms.join(" · ")}
                      </p>
                    </div>
                    <p className="flex flex-wrap items-center gap-2 rounded-lg border border-campaign/30 bg-campaign-tint px-3 py-2.5 text-sm font-semibold text-campaign-text">
                      <GiftIcon className="size-4" aria-hidden="true" />
                      {offer.card.gift}
                      <span className="font-normal">
                        ({offer.card.giftLabel})
                      </span>
                    </p>
                    <div>
                      <p className="mb-3 text-sm font-semibold">
                        What's in Pro
                      </p>
                      <ul className="grid gap-2.5">
                        {offer.card.featuresTop.map((f) => (
                          <li
                            key={f}
                            className="flex items-start gap-2.5 text-sm"
                          >
                            <CheckIcon
                              className="mt-0.5 size-4 shrink-0 text-brand-text"
                              aria-hidden="true"
                            />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <Collapsible>
                        <CollapsibleContent>
                          <ul className="mt-2.5 grid gap-2.5">
                            {offer.card.featuresMore.map((f) => (
                              <li
                                key={f}
                                className="flex items-start gap-2.5 text-sm"
                              >
                                <CheckIcon
                                  className="mt-0.5 size-4 shrink-0 text-brand-text"
                                  aria-hidden="true"
                                />
                                {f}
                              </li>
                            ))}
                          </ul>
                        </CollapsibleContent>
                        <CollapsibleTrigger className="group/more mt-3 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-brand-text hover:underline">
                          <span className="group-data-[panel-open]/more:hidden">
                            {offer.card.moreLabel}
                          </span>
                          <span className="hidden group-data-[panel-open]/more:inline">
                            Show fewer features
                          </span>
                          <ChevronDownIcon
                            className="size-4 transition-transform group-data-[panel-open]/more:rotate-180"
                            aria-hidden="true"
                          />
                        </CollapsibleTrigger>
                      </Collapsible>
                    </div>
                    <div className="rounded-lg bg-surface-soft p-4">
                      <p className="text-xs font-semibold">
                        {offer.card.ratesTitle}
                      </p>
                      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
                        {offer.card.rates.map(([k, v]) => (
                          <div key={k}>
                            <dt className="text-muted-foreground">{k}</dt>
                            <dd className="font-semibold">{v}</dd>
                          </div>
                        ))}
                      </dl>
                      <p className="mt-2 text-xs text-ink-strong">
                        {offer.card.ratesFree} ·{" "}
                        <a
                          href={site.links.messageCharges}
                          className="font-medium text-brand-text underline underline-offset-2"
                        >
                          {offer.card.ratesLink}
                        </a>
                      </p>
                      <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                        {offer.card.ratesNote}
                      </p>
                    </div>
                  </div>

                  {/* Form */}
                  <Suspense
                    fallback={
                      <div
                        className="min-h-[640px] rounded-lg border border-border bg-background shadow-card-float"
                        aria-busy="true"
                      />
                    }
                  >
                    <LeadForm />
                  </Suspense>
                </div>
              </div>
            </section>

            {/* 7. Free app vs official API */}
            <section className="bg-surface-soft py-16 md:py-20">
              <div className={cn(container, "flex flex-col gap-10")}>
                <SectionHeading
                  eyebrow={comparison.eyebrow}
                  lead={comparison.h2Lead}
                  accent={comparison.h2Accent}
                />
                <div className="overflow-hidden rounded-lg border border-border bg-background shadow-card-float">
                  <div className="hidden grid-cols-[0.8fr_1fr_1fr] border-b border-border text-sm font-semibold md:grid">
                    <div className="p-4">What you need</div>
                    <div className="p-4 text-muted-foreground">
                      {comparison.colApp}
                    </div>
                    <div className="bg-brand-tint p-4 text-brand-text">
                      {comparison.colApi}
                    </div>
                  </div>
                  <ul>
                    {comparison.rows.map(([need, app, api]) => (
                      <li
                        key={need}
                        className="grid border-b border-border last:border-0 md:grid-cols-[0.8fr_1fr_1fr]"
                      >
                        <p className="px-4 pt-4 text-sm font-semibold md:p-4">
                          {need}
                        </p>
                        <p className="flex gap-2 px-4 pt-2 text-sm text-muted-foreground md:p-4">
                          <XIcon
                            className="mt-0.5 size-4 shrink-0 text-danger-text"
                            aria-hidden="true"
                          />
                          <span>
                            <span className="sr-only md:hidden">
                              Free app:{" "}
                            </span>
                            {app}
                          </span>
                        </p>
                        <p className="bg-mint flex gap-2 px-4 pt-2 pb-4 text-sm md:bg-brand-tint/40 md:p-4">
                          <CheckIcon
                            className="mt-0.5 size-4 shrink-0 text-brand-text"
                            aria-hidden="true"
                          />
                          <span>
                            <span className="sr-only md:hidden">
                              Onbbits Pro:{" "}
                            </span>
                            {api}
                          </span>
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
                {!expired && (
                  <div className="bg-dark-glow-tr flex flex-col items-start justify-between gap-4 rounded-lg p-6 text-on-dark shadow-card-float sm:flex-row sm:items-center sm:p-8">
                    <div>
                      <p className="text-xl font-medium">
                        {comparison.band.title}
                      </p>
                      <p className="mt-1 text-on-dark-muted">
                        {comparison.band.text}
                      </p>
                    </div>
                    <a
                      href={CLAIM}
                      className={buttonVariants({
                        size: "lg",
                        className: "w-full sm:w-auto",
                      })}
                    >
                      {hero.cta}
                      <ArrowRightIcon
                        data-icon="inline-end"
                        aria-hidden="true"
                      />
                    </a>
                  </div>
                )}
              </div>
            </section>

            {/* 8. See it for your business */}
            <section className="py-16 md:py-24">
              <div
                className={cn(
                  container,
                  "grid items-center gap-12 lg:grid-cols-2"
                )}
              >
                <div className="flex flex-col gap-6">
                  <SectionHeading
                    align="left"
                    eyebrow={industries.eyebrow}
                    lead={industries.h2Lead}
                    accent={industries.h2Accent}
                    sub={industries.sub}
                  />
                  <ToggleGroup
                    aria-label="Choose your industry"
                    variant="outline"
                    spacing={2}
                    className="flex flex-wrap gap-2"
                    value={[industryId]}
                    onValueChange={(v) => {
                      if (v[0]) {
                        setIndustryId(v[0])
                        trackEvent("industry_sample", { industry: v[0] })
                      }
                    }}
                  >
                    {industries.items.map((i) => (
                      <ToggleGroupItem key={i.id} value={i.id}>
                        {i.label}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                  <p className="flex gap-3 rounded-lg border border-border bg-mint p-4 text-sm leading-relaxed">
                    <PlugIcon
                      className="mt-0.5 size-4 shrink-0 text-brand-text"
                      aria-hidden="true"
                    />
                    {industries.note}
                  </p>
                </div>
                <ChatPhone
                  business={industry.label}
                  items={industryItems}
                  liveKey={industry.id}
                />
              </div>
            </section>

            {/* 9. FAQ */}
            <section className="bg-mint py-16 md:py-20">
             <div
  className={cn(
    container,
    "grid items-start gap-10 lg:grid-cols-[0.8fr_1.2fr]"
  )}
>
                <div className="flex flex-col items-start gap-5 lg:sticky lg:top-28 lg:self-start">
                  <SectionHeading
                    align="left"
                    eyebrow={faq.eyebrow}
                    lead={faq.h2}
                  />
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Still unsure? Call us on{" "}
                    <a
                      href={site.phoneHref}
                      onClick={() =>
                        trackEvent("call_click", { location: "faq" })
                      }
                      className="font-medium text-brand-text underline underline-offset-2"
                    >
                      {site.phoneDisplay}
                    </a>{" "}
                    or write to{" "}
                    <a
                      href={`mailto:${site.email}`}
                      className="font-medium text-brand-text underline underline-offset-2"
                    >
                      {site.email}
                    </a>
                    .
                  </p>
                  {!expired && (
                    <a href={CLAIM} className={buttonVariants({ size: "lg" })}>
                      {hero.cta}
                    </a>
                  )}
                </div>
           <Accordion defaultValue={[0]} className="flex flex-col gap-3">
                  {faq.items.map(([q, a], i) => (
                    <AccordionItem
                      key={q}
                      value={i}
                      className="rounded-lg border border-border bg-background px-5 shadow-card-raised"
                    >
                      <AccordionTrigger className="py-5 text-base font-medium hover:no-underline">
                        {q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm leading-relaxed font-light text-ink-strong">
                        {a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </section>

            {/* 10. Final CTA */}
            <section className="px-3 py-16 sm:px-4 md:py-20">
              <div className="bg-dark-glow-tl mx-auto flex max-w-[1140px] flex-col items-center gap-5 rounded-lg px-6 py-14 text-center text-on-dark shadow-card-float sm:py-16">
                {!expired && (
                  <p className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold text-brand-bright">
                    <Diya className="-my-1 size-5" animated={false} />
                    {finalCta.tag}
                  </p>
                )}
                <h2 className="max-w-2xl text-[26px] leading-tight font-normal text-balance sm:text-[34px]">
                  {expired ? hero.ended.h1 : finalCta.h2}
                </h2>
                <p className="max-w-xl font-light text-on-dark-muted">
                  {expired ? hero.ended.sub : finalCta.sub}
                </p>
                <div className="flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row">
                  <a
                    href={expired ? site.links.pricing : CLAIM}
                    className={buttonVariants({
                      size: "lg",
                      className: "w-full sm:w-auto",
                    })}
                  >
                    {expired ? hero.ended.cta : hero.cta}
                    <ArrowRightIcon data-icon="inline-end" aria-hidden="true" />
                  </a>
                  <CallLink
                    location="final_cta"
                    dark
                    className="w-full sm:w-auto"
                  />
                </div>
              </div>
            </section>
          </>
        )}
      </main>

<SiteFooter/>
        </>
    )
}
export default DiwaliOffer;